import { supabase } from "@/integrations/supabase/client";

export const WEBHOOK_EVENTS = [
  { value: "document.exported", label: "Dokument exporterat" },
  { value: "document.created", label: "Dokument skapat" },
  { value: "safety_round.completed", label: "Skyddsrond genomförd" },
  { value: "incident.reported", label: "Tillbud rapporterat" },
  { value: "compliance.resolved", label: "Lagkrav kvitterat" },
  { value: "deadline.passed", label: "Deadline passerad" },
] as const;

export interface WebhookRow {
  id: string;
  name: string;
  target_url: string;
  event_types: string[];
  project_id: string | null;
  is_active: boolean;
  last_delivery_at: string | null;
  last_status_code: number | null;
  created_at: string;
}

export interface WebhookDeliveryRow {
  id: string;
  event_type: string;
  status_code: number | null;
  attempt: number;
  error_message: string | null;
  delivered_at: string | null;
  created_at: string;
}

export const generateSigningSecret = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return `whsec_${Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
};

export const listWebhooks = async (): Promise<WebhookRow[]> => {
  const { data, error } = await supabase
    .from("webhook_subscriptions")
    .select("id, name, target_url, event_types, project_id, is_active, last_delivery_at, last_status_code, created_at")
    .order("created_at", { ascending: false });
  if (error) return [];
  return (data || []) as WebhookRow[];
};

export const createWebhook = async (params: {
  name: string;
  targetUrl: string;
  eventTypes: string[];
  projectId: string | null;
}): Promise<{ row: WebhookRow; secret: string } | null> => {
  const { data: auth } = await supabase.auth.getUser();
  const ownerId = auth?.user?.id;
  if (!ownerId) return null;

  const secret = generateSigningSecret();
  const { data, error } = await supabase
    .from("webhook_subscriptions")
    .insert({
      owner_id: ownerId,
      name: params.name,
      target_url: params.targetUrl,
      event_types: params.eventTypes,
      project_id: params.projectId,
      signing_secret: secret,
    })
    .select("id, name, target_url, event_types, project_id, is_active, last_delivery_at, last_status_code, created_at")
    .single();

  if (error || !data) return null;
  return { row: data as WebhookRow, secret };
};

export const setWebhookActive = async (id: string, isActive: boolean): Promise<boolean> => {
  const { error } = await supabase
    .from("webhook_subscriptions")
    .update({ is_active: isActive })
    .eq("id", id);
  return !error;
};

export const deleteWebhook = async (id: string): Promise<boolean> => {
  const { error } = await supabase.from("webhook_subscriptions").delete().eq("id", id);
  return !error;
};

export const listWebhookDeliveries = async (
  subscriptionId: string
): Promise<WebhookDeliveryRow[]> => {
  const { data, error } = await supabase
    .from("webhook_deliveries")
    .select("id, event_type, status_code, attempt, error_message, delivered_at, created_at")
    .eq("subscription_id", subscriptionId)
    .order("created_at", { ascending: false })
    .limit(25);
  if (error) return [];
  return (data || []) as WebhookDeliveryRow[];
};

/** Skickar en händelse till alla aktiva prenumerationer via edge-funktionen. */
export const dispatchWebhookEvent = async (params: {
  projectId: string | null;
  eventType: string;
  payload: Record<string, unknown>;
}): Promise<void> => {
  try {
    await supabase.functions.invoke("webhook-dispatch", {
      body: {
        project_id: params.projectId,
        event_type: params.eventType,
        payload: params.payload,
      },
    });
  } catch {
    /* best effort */
  }
};
